/**
 * Organisation Onboarding API Service
 * 
 * Handles the organisation creation + admin linking flow.
 * Single API call to backend which atomically:
 *   1. Creates Organisation
 *   2. Creates Admin User (or links existing)
 *   3. Links admin to organisation
 *   4. Marks onboarding as completed
 */

import apiClient from './apiClient';
import { normalizeIndianMobile } from '../../utils/phone';

/**
 * Submit organisation onboarding data.
 * Backend creates org + links the current user as admin.
 *
 * @param {Object} data - Validated form data
 * @param {string} data.first_name
 * @param {string} data.last_name
 * @param {string} data.organisation_name
 * @param {string} data.organisation_type
 * @param {string} data.city
 * @param {string} data.state
 * @param {string} data.mobile_number
 * @param {string} data.email
 * @param {string[]} data.working_days
 * @param {string} data.opening_time - HH:mm format
 * @param {string} data.closing_time - HH:mm format
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 */
export const submitOrgOnboarding = async (data) => {
  try {
    // Normalize mobile before sending
    let normalizedMobile = data.mobile_number;
    try {
      const { e164 } = normalizeIndianMobile(data.mobile_number);
      normalizedMobile = e164;
    } catch {
      // Validation should have caught this; send as-is
    }

    const payload = {
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
      organisation_name: data.organisation_name.trim(),
      organisation_type: data.organisation_type,
      city: data.city.trim(),
      state: data.state.trim(),
      mobile_number: normalizedMobile,
      email: data.email.trim().toLowerCase(),
      working_days: data.working_days,
      opening_time: data.opening_time,
      closing_time: data.closing_time,
    };

    const response = await apiClient.post('/api/organisation/profile', payload);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('[OrgOnboardingAPI] Submission error:', error);

    let errorMessage = 'Failed to create organisation. Please try again.';

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 400) {
      errorMessage = 'Invalid data. Please check your information.';
    } else if (error.response?.status === 401) {
      errorMessage = 'Session expired. Please login again.';
    } else if (error.response?.status === 409) {
      errorMessage = 'Organisation already exists for this account.';
    } else if (error.response?.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};
